import React, { useEffect, useState } from "react";
import { PageLoader, EmptyState, StatusBadge } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { IdentificationCard, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";

export default function AdminOrganizers() {
  const [orgs, setOrgs] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    const q = status === "all" ? "" : `?status=${status}`;
    api.get(`/admin/organizers${q}`).then((r) => setOrgs(r.data.organizers)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [status]);

  const act = async (uid, action) => {
    try { const { data } = await api.post(`/admin/organizers/${uid}/${action}`); toast.success(data.message); setDetail(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><div className="overline mb-1">Verification</div><h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Organizers</h1></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="org-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "admin_approval_pending", "approved", "agreement_pending", "verification_pending", "correction_required", "rejected", "suspended", "incomplete"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <PageLoader /> : orgs.length === 0 ? (
        <EmptyState icon={IdentificationCard} title="No organizers" subtitle="Organizers will appear here as they onboard." testid="no-orgs" />
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border">
          {orgs.map((o) => (
            <button key={o.user_id} onClick={() => setDetail(o)} data-testid={`org-row-${o.user_id}`} className="w-full flex items-center justify-between p-4 hover:bg-muted/40 text-left">
              <div>
                <div className="font-medium">{o.company_name || o.user?.name || "—"}</div>
                <div className="text-sm text-muted-foreground">{o.user?.email} · GST {o.gst_number || "—"}</div>
              </div>
              <StatusBadge status={o.status} />
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="org-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{detail.company_name || detail.user?.name}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <Row l="Status" v={<StatusBadge status={detail.status} />} />
                <Row l="Owner" v={`${detail.user?.name} (${detail.user?.email})`} />
                <Row l="Business type" v={detail.business_type} />
                <Row l="Address" v={detail.registered_address} />
                <Row l="GST" v={<>{detail.gst_number} {detail.gst_verified && <CheckCircle className="inline text-emerald-600" size={14} />}</>} />
                <Row l="PAN" v={<>{detail.pan_number} {detail.pan_verified && <CheckCircle className="inline text-emerald-600" size={14} />}</>} />
                <Row l="Signatory" v={detail.authorized_signatory} />
                <Row l="Payout" v={detail.payout ? `${detail.payout.bank_name} ${detail.payout.account_number_masked} (${detail.payout.status})` : "Not set"} />
                <Row l="Agreement" v={detail.agreement ? `Signed v${detail.agreement.version}` : "Not signed"} />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button onClick={() => act(detail.user_id, "approve")} data-testid="approve-org" className="rounded-full">Approve</Button>
                <Button onClick={() => act(detail.user_id, "request-correction")} variant="outline" className="rounded-full">Request correction</Button>
                <Button onClick={() => act(detail.user_id, "suspend")} variant="outline" className="rounded-full">Suspend</Button>
                <Button onClick={() => act(detail.user_id, "reject")} variant="outline" className="rounded-full text-destructive border-destructive/30" data-testid="reject-org">Reject</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Row({ l, v }) { return <div className="flex justify-between gap-4 py-1 border-b border-border/60"><span className="text-muted-foreground">{l}</span><span className="text-right font-medium">{v}</span></div>; }
